import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';
import { Download, RefreshCw, Calendar, BarChart3, BarChart4, Loader2 } from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { format, subDays, parseISO } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';

interface BillingUsageChartsProps {
  organisationId: string;
}

export const BillingUsageCharts: React.FC<BillingUsageChartsProps> = ({ 
  organisationId
}) => {
  const [loading, setLoading] = useState(false);
  const [usageData, setUsageData] = useState<any>({
    total: 0,
    by_restaurant: []
  });
  const [timeframe, setTimeframe] = useState<'7days' | '30days' | '90days'>('30days');
  const [chartType, setChartType] = useState<'bar' | 'line' | 'pie'>('bar');
  
  // Colors for the charts
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#A259FF', '#4CAF50', '#FB6D42', '#46BFBD'];
  
  // Load usage data when component mounts or organisationId/timeframe changes
  useEffect(() => {
    if (organisationId) {
      loadUsageData();
    }
  }, [organisationId, timeframe]);

  // Format a date range based on the selected timeframe
  const getDateRange = () => {
    const endDate = new Date();
    let startDate;
    
    switch (timeframe) {
      case '7days':
        startDate = subDays(endDate, 7);
        break;
      case '30days':
        startDate = subDays(endDate, 30);
        break;
      case '90days':
        startDate = subDays(endDate, 90);
        break;
      default:
        startDate = subDays(endDate, 30);
    }
    
    return {
      start_date: format(startDate, 'yyyy-MM-dd'),
      end_date: format(endDate, 'yyyy-MM-dd')
    };
  };

  // Load usage data directly from database using RPC function
  const loadUsageData = async () => {
    setLoading(true);
    try {
      const { start_date, end_date } = getDateRange();
      
      console.log(`Loading SMS usage data for organisation ${organisationId} from ${start_date} to ${end_date}`);
      
      // Get all restaurants for this organisation
      const { data: restaurants, error: restError } = await supabase
        .from('restaurants_master')
        .select('restaurant_id, restaurant_name')
        .eq('organisation_id', organisationId);
      
      if (restError) {
        console.error('Error fetching restaurants:', restError);
        throw new Error(`Failed to fetch restaurants: ${restError.message}`);
      }
      
      const restaurantIds = restaurants?.map(r => r.restaurant_id) || [];
      
      if (restaurantIds.length === 0) {
        console.log('No restaurants found for this organisation');
        setUsageData({
          total: 0,
          by_restaurant: []
        });
        return;
      }
      
      // Call the RPC function to get SMS usage data
      const { data: usageRecords, error: usageError } = await supabase.rpc(
        'get_sms_usage_from_campaigns',
        {
          restaurant_ids: restaurantIds,
          start_date,
          end_date
        }
      );
      
      if (usageError) {
        console.error('Error calling get_sms_usage_from_campaigns:', usageError);
        throw new Error(`Failed to get SMS usage: ${usageError.message}`);
      }
      
      // If we got data back, process it
      if (usageRecords) {
        // Calculate total usage
        const totalUsage = usageRecords.reduce((sum, record) => sum + (record.units || 0), 0);
        
        // Group by restaurant
        const usageByRestaurant = [];
        const restaurantMap = new Map();
        
        // First organize data by restaurant
        for (const record of usageRecords) {
          if (!restaurantMap.has(record.restaurant_id)) {
            restaurantMap.set(record.restaurant_id, {
              restaurant_id: record.restaurant_id,
              restaurant_name: record.restaurant_name || 'Unknown Restaurant',
              total_units: 0,
              records: []
            });
          }
          
          const restaurant = restaurantMap.get(record.restaurant_id);
          restaurant.total_units += (record.units || 0);
          restaurant.records.push(record);
        }
        
        // Convert the map to an array and sort by restaurant name
        for (const restaurantData of restaurantMap.values()) {
          usageByRestaurant.push(restaurantData);
        }
        
        usageByRestaurant.sort((a, b) => a.restaurant_name.localeCompare(b.restaurant_name));
        
        // Set the usage data
        setUsageData({
          total: totalUsage,
          by_restaurant: usageByRestaurant,
          source: 'campaigns'
        });
      } else {
        // No data returned, set empty usage data
        setUsageData({
          total: 0,
          by_restaurant: [],
          source: 'none'
        });
      }
    } catch (error) {
      console.error('Error loading usage data:', error);
      toast({
        title: "Failed to load usage data",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive"
      });
      
      // Set empty data to prevent UI errors
      setUsageData({
        total: 0,
        by_restaurant: []
      });
    } finally {
      setLoading(false);
    }
  };

  // Prepare data for the restaurant comparison chart
  const getRestaurantComparisonData = () => {
    return usageData.by_restaurant.map((restaurant: any) => ({
      name: restaurant.restaurant_name,
      value: restaurant.total_units
    }));
  };

  // Prepare data for the timeline chart
  const getTimelineData = () => {
    // Create a map to aggregate data by date
    const dateMap = new Map();
    
    // Process each restaurant's records
    usageData.by_restaurant.forEach((restaurant: any) => {
      (restaurant.records || []).forEach((record: any) => {
        const date = record.date;
        const current = dateMap.get(date) || { date, total: 0 };
        current.total += record.units;
        current[restaurant.restaurant_name] = record.units;
        dateMap.set(date, current);
      });
    });
    
    // Convert map to array and sort by date
    const result = Array.from(dateMap.values());
    result.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    
    return result;
  };

  // Export usage data as CSV
  const exportAsCSV = () => {
    try {
      // Create CSV header
      let csvContent = "Restaurant,Date,SMS Segments\n";
      
      // Add data rows
      usageData.by_restaurant.forEach((restaurant: any) => {
        (restaurant.records || []).forEach((record: any) => {
          csvContent += `"${restaurant.restaurant_name}","${record.date}",${record.units}\n`;
        });
      });
      
      // Create and download the file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `sms-usage-${organisationId}-${format(new Date(), 'yyyy-MM-dd')}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast({
        title: "Export Successful",
        description: "Usage data has been downloaded as CSV",
      });
    } catch (error) {
      console.error('Error exporting CSV:', error);
      toast({
        title: "Failed to export data",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive"
      });
    }
  };

  // Format date for display in charts
  const formatChartDate = (dateString: string) => {
    try {
      return format(parseISO(dateString), 'MMM dd');
    } catch (e) {
      return dateString;
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <CardTitle>SMS Usage Reports</CardTitle>
              <CardDescription>
                View and analyze SMS usage across restaurants
              </CardDescription>
            </div>
            
            <div className="flex flex-wrap gap-2">
              {/* Timeframe selection */}
              <div className="flex rounded-md shadow-sm">
                <Button 
                  variant={timeframe === '7days' ? 'default' : 'outline'} 
                  size="sm"
                  onClick={() => setTimeframe('7days')}
                  className="rounded-l-md rounded-r-none"
                >
                  7 days
                </Button>
                <Button 
                  variant={timeframe === '30days' ? 'default' : 'outline'} 
                  size="sm"
                  onClick={() => setTimeframe('30days')}
                  className="rounded-none border-l-0 border-r-0"
                >
                  30 days
                </Button>
                <Button 
                  variant={timeframe === '90days' ? 'default' : 'outline'} 
                  size="sm"
                  onClick={() => setTimeframe('90days')}
                  className="rounded-r-md rounded-l-none"
                >
                  90 days
                </Button>
              </div>
              
              {/* Chart type selection */}
              <div className="flex rounded-md shadow-sm">
                <Button
                  variant={chartType === 'bar' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setChartType('bar')}
                  className="rounded-l-md rounded-r-none"
                >
                  <BarChart3 className="h-4 w-4" />
                </Button>
                <Button
                  variant={chartType === 'line' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setChartType('line')}
                  className="rounded-none border-l-0 border-r-0"
                >
                  <BarChart4 className="h-4 w-4" />
                </Button>
                <Button
                  variant={chartType === 'pie' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setChartType('pie')}
                  className="rounded-r-md rounded-l-none"
                >
                  <Calendar className="h-4 w-4" />
                </Button>
              </div>
              
              {/* Actions */}
              <Button 
                variant="outline" 
                size="sm" 
                onClick={loadUsageData}
                disabled={loading}
              >
                <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              
              <Button 
                variant="outline" 
                size="sm" 
                onClick={exportAsCSV}
                disabled={loading || usageData.total === 0}
              >
                <Download className="mr-2 h-4 w-4" />
                Export CSV
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-12 text-center">
              <Loader2 className="mx-auto h-8 w-8 animate-spin text-muted-foreground" />
              <p className="mt-4">Loading usage data...</p>
            </div>
          ) : usageData.total > 0 ? (
            <div className="space-y-8">
              {/* Summary metrics */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">
                      Total SMS Usage
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {usageData.total.toLocaleString()} segments
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Across all restaurants in the selected period
                    </p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">
                      Active Restaurants
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {usageData.by_restaurant.filter((r: any) => r.total_units > 0).length}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Restaurants that sent SMS in this period
                    </p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">
                      Time Period
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-lg font-medium">
                      {timeframe === '7days' ? 'Last 7 days' : 
                       timeframe === '30days' ? 'Last 30 days' : 
                       'Last 90 days'}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {format(parseISO(getDateRange().start_date), 'MMM dd, yyyy')} - 
                      {format(parseISO(getDateRange().end_date), 'MMM dd, yyyy')}
                    </p>
                  </CardContent>
                </Card>
              </div>
              
              {/* Timeline chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">SMS Usage Timeline</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      {chartType === 'line' ? (
                        <LineChart
                          data={getTimelineData()}
                          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis 
                            dataKey="date" 
                            tickFormatter={formatChartDate}
                          />
                          <YAxis />
                          <Tooltip 
                            labelFormatter={(value) => format(parseISO(value), 'MMMM dd, yyyy')}
                          />
                          <Legend />
                          <Line 
                            type="monotone" 
                            dataKey="total" 
                            stroke="#8884d8" 
                            name="Total SMS"
                            strokeWidth={2}
                          />
                        </LineChart>
                      ) : chartType === 'bar' ? (
                        <BarChart
                          data={getTimelineData()}
                          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis 
                            dataKey="date" 
                            tickFormatter={formatChartDate}
                          />
                          <YAxis />
                          <Tooltip 
                            labelFormatter={(value) => format(parseISO(value), 'MMMM dd, yyyy')}
                          />
                          <Legend />
                          <Bar 
                            dataKey="total" 
                            fill="#8884d8" 
                            name="Total SMS" 
                          />
                        </BarChart>
                      ) : (
                        <PieChart>
                          <Pie
                            data={getRestaurantComparisonData()}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            outerRadius={130}
                            fill="#8884d8"
                            dataKey="value"
                            nameKey="name"
                            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                          >
                            {getRestaurantComparisonData().map((entry: any, index: number) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value) => [`${value} segments`, 'Usage']} />
                          <Legend />
                        </PieChart>
                      )}
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
              
              {/* Restaurant comparison */}
              {chartType !== 'pie' && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Restaurant Comparison</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={getRestaurantComparisonData()}
                          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                          layout="vertical"
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" />
                          <YAxis 
                            type="category" 
                            dataKey="name" 
                            width={150}
                          />
                          <Tooltip formatter={(value) => [`${value} segments`, 'Usage']} />
                          <Legend />
                          <Bar 
                            dataKey="value" 
                            name="SMS Segments" 
                            fill="#8884d8"
                          >
                            {getRestaurantComparisonData().map((entry: any, index: number) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : (
            <div className="py-12 text-center border rounded-lg">
              <RefreshCw className="mx-auto h-12 w-12 text-muted-foreground" />
              <h3 className="mt-4 text-lg font-medium">No Usage Data Available</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                No SMS usage data was found for the selected time period.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};